import { CheckoutSessionErrorPage } from "@circle-react/components/Paywalls/Checkout/components/CheckoutSessionErrorPage";
import { useCheckoutSessionOwnership } from "@circle-react/components/Paywalls/Checkout/contexts/CheckoutSessionOwnershipContext";
import {
  getCheckoutSessionReferrer,
  isTerminalCheckoutSessionCreationError,
  type CheckoutSessionOwnershipToken,
} from "@circle-react/components/Paywalls/Checkout/sessionOwnership";
import { checkoutSession as checkoutSessionUrls } from "@circle-react/helpers/urlHelpers/internalApi/paywalls/checkout/sessions";
import {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useCallback,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { CookiesProvider } from "react-cookie";
import { useQueryClient } from "react-query";

import { getCheckoutSessionQueryKey } from "@/react/components/Paywalls/Checkout/helpers/sessions/getCheckoutSessionQueryKey";
import { updateSessionCache } from "@/react/components/Paywalls/Checkout/helpers/sessions/updateSessionCache";
import { useCreateCheckoutSession } from "@/react/components/Paywalls/Checkout/hooks/sessions/useCreateCheckoutSession";
import { useFetchCheckoutSession } from "@/react/components/Paywalls/Checkout/hooks/sessions/useFetchCheckoutSession";
import { useJustCompletedSteps } from "@/react/components/Paywalls/Checkout/hooks/useJustCompletedSteps";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { getQueryParam } from "@/react/helpers/urlHelpers";
import type {
  CheckoutSession,
  PaymentValidationParams,
} from "@/react/types/Paywalls/Checkout/Session";

import { CheckoutError } from "../CheckoutError";
import { useBeginCheckoutTracking } from "../hooks/conversionTracking/useBeginCheckoutTracking";
import { useConversionTrackingFunnelState } from "../hooks/conversionTracking/useConversionTrackingFunnelState";
import { useTrackConversionEvent } from "../hooks/conversionTracking/useTrackConversionEvent";
import type { TrackConversionEvent } from "../hooks/conversionTracking/useTrackConversionEvent";
import { useSessionOwnership } from "../hooks/useSessionOwnership";

interface CheckoutSessionContextValue {
  conversionTrackingFiredRef: MutableRefObject<string | null>;
  error: Error | null;
  funnelUuid: string;
  ga4FiredStepIdsRef: MutableRefObject<Set<number>>;
  isLoading: boolean;
  isRedirectReturnMount: boolean;
  justCompletedStepIds: Set<number>;
  markStepJustCompleted: (stepId: number) => void;
  ownershipToken: CheckoutSessionOwnershipToken | null;
  refetchSession: () => Promise<CheckoutSession>;
  refreshSession: (rejectedSessionId?: number) => Promise<void>;
  session: CheckoutSession | null;
  trackConversionEvent: TrackConversionEvent;
}

const CheckoutSessionContext = createContext<
  CheckoutSessionContextValue | undefined
>(undefined);

CheckoutSessionContext.displayName = "CheckoutSessionContext";

interface CheckoutSessionProviderProps {
  children: ReactNode;
  paymentValidationParams?: PaymentValidationParams;
  paywallId: number;
  sessionId?: number | null;
  stepId?: number | null;
}

const CheckoutSessionProviderContent = ({
  children,
  paywallId,
  sessionId,
  stepId,
  paymentValidationParams,
}: CheckoutSessionProviderProps) => {
  const queryClient = useQueryClient();

  const ga4FiredStepIdsRef = useRef(new Set<number>());
  const { justCompletedStepIds, markStepJustCompleted } =
    useJustCompletedSteps();

  const {
    mutateAsync: createSession,
    isPending: isCreating,
    error: createError,
  } = useCreateCheckoutSession();

  const {
    data: fetchedSession,
    isFetching,
    error: fetchError,
    refetch,
  } = useFetchCheckoutSession({
    paywallId,
    sessionId,
    stepId,
    paymentValidationParams,
  });

  const wasSessionFetched = !!stepId || !!sessionId;

  const cachedSession = queryClient.getQueryData<CheckoutSession>(
    getCheckoutSessionQueryKey(paywallId),
  );

  const session = cachedSession || fetchedSession;
  const { getTokenForSession } = useCheckoutSessionOwnership();
  const ownershipToken = getTokenForSession(session?.id);

  const { conversionTrackingFiredRef, funnelUuid, isRedirectReturnMount } =
    useConversionTrackingFunnelState(paywallId, !!session);

  const { trackConversionEvent } = useTrackConversionEvent(
    conversionTrackingFiredRef,
    session?.settings.conversion_tracking_settings?.meta_pixel_id,
    session?.settings.conversion_tracking_settings?.meta_fbc,
  );
  const isWaiting =
    !wasSessionFetched && !!paywallId && !session && !createError;
  const isLoading = wasSessionFetched ? isFetching : isCreating || isWaiting;
  const error = wasSessionFetched ? (session ? null : fetchError) : createError;
  const hasRetriedSessionInitializationRef = useRef(false);
  const refetchSession = useCallback(async (): Promise<CheckoutSession> => {
    if (wasSessionFetched) {
      const result = await refetch();
      if (result.error) {
        throw result.error;
      }
      if (!result.data) {
        throw CheckoutError.generic("CheckoutSessionContext.refetchSession");
      }
      return result.data;
    }

    if (!session?.id || !paywallId) {
      throw CheckoutError.generic("CheckoutSessionContext.refetchSession");
    }
    const url = checkoutSessionUrls.show({
      paywallId,
      sessionId: session.id,
    });
    const freshSession = await queryClient.fetchQuery<CheckoutSession>({
      queryKey: getCheckoutSessionQueryKey(paywallId),
      queryFn: () => reactQueryGet<CheckoutSession>(url),
      staleTime: 0,
    });
    if (!freshSession) {
      throw CheckoutError.generic("CheckoutSessionContext.refetchSession");
    }
    updateSessionCache(queryClient, paywallId, freshSession);
    return freshSession;
  }, [wasSessionFetched, refetch, session?.id, paywallId, queryClient]);
  const refreshSession = useCallback(
    async (rejectedSessionId?: number) => {
      if (paywallId) {
        const eventIdParam = getQueryParam("event_id");
        await createSession({
          paywallId,
          referrer: getCheckoutSessionReferrer(),
          eventId: eventIdParam ? parseInt(eventIdParam) : undefined,
          rejectedSessionId,
          returnTo: getQueryParam("return_to") || undefined,
        });
      }
    },
    [paywallId, createSession],
  );
  useEffect(() => {
    if (
      !wasSessionFetched &&
      paywallId &&
      !session &&
      !isCreating &&
      !hasRetriedSessionInitializationRef.current &&
      !isTerminalCheckoutSessionCreationError(createError)
    ) {
      const eventIdParam = getQueryParam("event_id");
      void createSession({
        paywallId,
        referrer: getCheckoutSessionReferrer(),
        eventId: eventIdParam ? parseInt(eventIdParam) : undefined,
        returnTo: getQueryParam("return_to") || undefined,
      });
    }
  }, [
    wasSessionFetched,
    paywallId,
    session,
    isCreating,
    createError,
    createSession,
  ]);

  useBeginCheckoutTracking({
    session,
    wasSessionFetched,
    funnelUuid,
    paywallId,
    trackConversionEvent,
  });

  const value = useMemo<CheckoutSessionContextValue>(
    () => ({
      session: session || null,
      isLoading,
      error: error instanceof Error ? error : null,
      ga4FiredStepIdsRef,
      conversionTrackingFiredRef,
      trackConversionEvent,
      funnelUuid,
      isRedirectReturnMount,
      justCompletedStepIds,
      markStepJustCompleted,
      ownershipToken,
      refetchSession,
      refreshSession,
    }),
    [
      session,
      isLoading,
      error,
      trackConversionEvent,
      funnelUuid,
      isRedirectReturnMount,
      justCompletedStepIds,
      markStepJustCompleted,
      ownershipToken,
      refetchSession,
      refreshSession,
    ],
  );

  const { isSessionOwnershipValid } = useSessionOwnership({
    session,
  });

  if (error || (!isLoading && !session) || !isSessionOwnershipValid) {
    return (
      <CheckoutSessionErrorPage
        createError={createError}
        hasRetriedRef={hasRetriedSessionInitializationRef}
        refreshSession={refreshSession}
      />
    );
  }

  return (
    <CheckoutSessionContext.Provider value={value}>
      {children}
    </CheckoutSessionContext.Provider>
  );
};

export const CheckoutSessionProvider = (
  props: CheckoutSessionProviderProps,
) => (
  <CookiesProvider>
    <CheckoutSessionProviderContent {...props} />
  </CookiesProvider>
);

const EMPTY_GA4_FIRED_REF: MutableRefObject<Set<number>> = {
  current: new Set<number>(),
};

const EMPTY_CONVERSION_TRACKING_FIRED_REF: MutableRefObject<string | null> = {
  current: null,
};
const EMPTY_TRACK_CONVERSION_EVENT: TrackConversionEvent = () => undefined;
const EMPTY_JUST_COMPLETED_STEP_IDS: Set<number> = new Set<number>();

export const useCheckoutSession = (): CheckoutSessionContextValue => {
  const context = useContext(CheckoutSessionContext);

  if (context === undefined) {
    return {
      session: null,
      isLoading: false,
      error: null,
      ga4FiredStepIdsRef: EMPTY_GA4_FIRED_REF,
      conversionTrackingFiredRef: EMPTY_CONVERSION_TRACKING_FIRED_REF,
      trackConversionEvent: EMPTY_TRACK_CONVERSION_EVENT,
      funnelUuid: "",
      isRedirectReturnMount: false,
      justCompletedStepIds: EMPTY_JUST_COMPLETED_STEP_IDS,
      markStepJustCompleted: () => {},
      ownershipToken: null,
      refetchSession: () =>
        Promise.reject(
          CheckoutError.generic("CheckoutSessionContext.refetchSession"),
        ),
      refreshSession: async () => {},
    };
  }

  return context;
};
